import type { SessionInteraction } from '../game/input/SessionInteraction';
import { t } from '../i18n';
import { ContextualMenu, type ContextualMenuAction } from './ContextualMenu';

export interface SessionMenuAction extends ContextualMenuAction {
  readonly confirmation?: Readonly<{ title: string; description: string }>;
}

export interface SessionMenuContext {
  readonly title: string;
  readonly description: string;
  readonly actions?: readonly ContextualMenuAction[];
  readonly terminalAction: SessionMenuAction;
}

export class SessionMenu {
  readonly #interaction: SessionInteraction;
  readonly #menu: ContextualMenu;
  readonly #resume: HTMLElement;
  readonly #context: () => SessionMenuContext;
  readonly #unsubscribe: () => void;
  #confirmActionId: string | null = null;

  constructor(host: HTMLElement, interaction: SessionInteraction, context: () => SessionMenuContext) {
    this.#interaction = interaction;
    this.#context = context;
    this.#menu = new ContextualMenu(host, () => interaction.back(), () => interaction.nativeEscapeInFlight);
    this.#resume = document.createElement('div');
    this.#resume.className = 'session-resume';
    this.#resume.hidden = true;
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = t('common:loading.continue');
    button.addEventListener('click', () => interaction.continueFromGesture());
    this.#resume.append(button);
    host.append(this.#resume);
    interaction.setMenuBackHandler(() => {
      if (!this.#confirmActionId) return false;
      this.#confirmActionId = null;
      this.refresh();
      return true;
    });
    this.#unsubscribe = interaction.subscribe(() => {
      this.#confirmActionId = null;
      this.refresh();
    });
  }

  refresh(): void {
    this.#resume.hidden = this.#interaction.context !== 'resume';
    if (this.#interaction.context !== 'menu') { this.#menu.hide(); return; }
    const context = this.#context();
    const terminal = context.terminalAction;
    if (this.#confirmActionId) {
      if (this.#confirmActionId !== terminal.id || !terminal.confirmation) {
        this.#confirmActionId = null;
        this.refresh();
        return;
      }
      this.#menu.show({ title: terminal.confirmation.title, description: terminal.confirmation.description,
        initialActionId: 'stay', actions: [
          { id: 'stay', label: t('common:actions.cancel'), primary: true,
            run: () => { this.#confirmActionId = null; this.refresh(); } },
          terminal,
        ] });
      return;
    }
    this.#menu.show({ title: context.title, description: context.description, initialActionId: 'continue', actions: [
      { id: 'continue', label: t('common:actions.continue'), primary: true, run: () => this.#interaction.continueFromGesture() },
      ...(context.actions ?? []),
      { ...terminal, run: () => {
        if (!terminal.confirmation) return terminal.run();
        this.#confirmActionId = terminal.id;
        this.refresh();
      } },
    ] });
  }

  dispose(): void {
    this.#unsubscribe();
    this.#interaction.setMenuBackHandler(null);
    this.#menu.dispose();
    this.#resume.remove();
  }
}
